import React from 'react';
import { Button } from '@blueprintjs/core';
import { Suggest } from '@blueprintjs/select';
import { observable, action, toJS } from 'mobx';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import numeral from 'numeral';
import { Api } from 'services/Api';
import debounce from 'services/debounce';

// Pick a product or service from the price book: a search field until one is
// picked, then its name (click to pick another). The menu says what it found
// and shows everything we know about each item: whether it's labor or a
// material, its category, part number and supplier, its description, its
// price and markup. Styles: .ps-menu in document-editor.scss.
//
// `kind` ('labour' | 'material') limits the search to that kind of item.

const TYPES = { labour: 'Service', material: 'Products (Non-inventory)' };
const NOUNS = { labour: ['service', 'services'], material: ['product', 'products'] };

const price = (value) => (value === null || value === undefined || value === '' ? null : numeral(value).format('$0,0.00'));
const isLabour = (attributes) => attributes.product_service_type === 'Service';
const nameOf = (product) => (product && ((product.attributes && product.attributes.name) || product.name)) || '';

export const ProductRow = ({ product, active, handleClick, kind }) => {
  const a = product.attributes;
  const amount = price(a.unit_price);
  const markup = parseFloat(a.mark_up);
  const category = a.product_category && (a.product_category.name || (a.product_category.attributes && a.product_category.attributes.name));
  const supplier = a.supplier_name || (a.supplier && a.supplier.name);
  return (
    <li
      key={product.id}
      role="option"
      aria-selected={active}
      className={`ps-row${active ? ' is-active' : ''}`}
      onMouseDown={(event) => event.preventDefault()}
      onClick={handleClick}
    >
      <div className="ps-row__main">
        <span className="ps-row__name">{a.name}</span>
        <span className="ps-row__facts">
          {!kind && <span className={`ps-chip ps-chip--${isLabour(a) ? 'labour' : 'material'}`}>{isLabour(a) ? 'Labor' : 'Material'}</span>}
          {category && <span>{category}</span>}
          {a.part_number && <span className="ps-row__part">#{a.part_number}</span>}
          {supplier && <span>{supplier}</span>}
        </span>
        {a.description && <span className="ps-row__description">{a.description}</span>}
      </div>
      <div className="ps-row__side">
        <span className="ps-row__price">
          {amount || <span className="ps-row__none">No price</span>}
          {amount && isLabour(a) && <span className="ps-row__unit">/hr</span>}
        </span>
        {Number.isFinite(markup) && markup !== 1 && markup !== 0 && <span className="ps-row__markup">Markup ×{markup}</span>}
      </div>
    </li>
  );
};

@observer
class ProductServiceAutocomplete extends React.Component {
  @observable loading = false;
  @observable failed = false;
  @observable results = [];
  @observable query = '';
  @observable selectedProduct = null;
  request = 0;

  componentDidMount() {
    const { selectedProductId, value } = this.props;
    if (selectedProductId) {
      Api.post('/product_services/search', { search: { term: '*', where: { id: selectedProductId }, per_page: 50, page: 1, order: { name: 'asc' } } })
        .then(action((response) => { this.selectedProduct = response.data.data[0] || null; }))
        .catch(() => {});
    }
    if (value) this.selectedProduct = value;
  }

  componentDidUpdate() {
    if (this.props.value) this.selectedProduct = this.props.value;
  }

  search = debounce((query) => {
    const id = ++this.request;
    const { kind } = this.props;
    const search = { term: query.trim() || '*', per_page: 20, page: 1, order: { name: 'asc' } };
    if (TYPES[kind]) search.where = { product_service_type: TYPES[kind] };
    Api.post('/product_services/search', { search })
      .then(action((response) => {
        if (id !== this.request) return;
        this.loading = false;
        this.failed = false;
        this.results = response.data.data || [];
      }))
      .catch(action(() => {
        if (id !== this.request) return;
        this.loading = false;
        this.failed = true;
      }));
  }, 250);

  @action
  onQueryChange = (query) => {
    this.query = query;
    this.loading = true;
    this.failed = false;
    this.search(query);
  };

  @action
  onProductSelect = (product) => {
    this.selectedProduct = product;
    this.props.onProductSelect(product);
  };

  @action
  clear = () => {
    this.selectedProduct = null;
    this.props.onProductSelect(null);
  };

  renderMenu = ({ items, itemsParentRef, renderItem }) => {
    const { kind } = this.props;
    const [one, many] = NOUNS[kind] || ['item', 'items'];
    const typed = this.query.trim();
    const rows = items.map(renderItem).filter(Boolean);
    let status;
    if (this.loading) status = typed ? `Searching for “${typed}”…` : `Loading ${many}…`;
    else if (this.failed) status = `Couldn't load ${many}. Check your connection and type again.`;
    else if (!typed) status = rows.length ? `Pick a ${one}, or type to search` : `No ${many} in your price book yet`;
    else if (rows.length) status = `${rows.length} ${rows.length === 1 ? one : many} match “${typed}”`;
    else status = `No ${one} matches “${typed}”. Check the spelling or add it to your price book.`;
    return (
      <div className="ps-menu">
        <div className="ps-menu__status" role="status">{status}</div>
        {rows.length > 0 && !this.loading && (
          <ul className="ps-menu__list" ref={itemsParentRef} role="listbox" aria-label={many}>
            {rows}
          </ul>
        )}
      </div>
    );
  };

  render() {
    const { inputProps, kind } = this.props;
    const selected = this.selectedProduct;
    if (selected) {
      return (
        <div>
          <Button
            text={nameOf(selected)}
            rightIcon="cross"
            className="bp3-text-overflow"
            style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}
            title={`${nameOf(selected)}: click to pick another`}
            onClick={this.clear}
          />
        </div>
      );
    }
    return (
      <div>
        <Suggest
          inputProps={{ placeholder: kind === 'labour' ? 'Choose a service' : kind === 'material' ? 'Choose a product' : 'Search materials & labor…', ...inputProps }}
          closeOnSelect
          openOnKeyDown={false}
          resetOnQuery
          resetOnSelect
          query={this.query}
          popoverProps={{ minimal: true, usePortal: true, popoverClassName: 'ps-menu-popover', onOpening: () => { if (!this.results.length && !this.loading) this.onQueryChange(this.query); } }}
          onQueryChange={this.onQueryChange}
          items={toJS(this.results)}
          itemRenderer={(product, { handleClick, modifiers }) => (
            <ProductRow key={product.id} product={product} active={modifiers.active} handleClick={handleClick} kind={kind} />
          )}
          itemListRenderer={this.renderMenu}
          inputValueRenderer={(product) => nameOf(product)}
          onItemSelect={this.onProductSelect}
          noResults={null}
        />
      </div>
    );
  }
}

ProductServiceAutocomplete.propTypes = {
  inputProps: PropTypes.object,
  onProductSelect: PropTypes.func.isRequired,
  kind: PropTypes.oneOf(['labour', 'material']),
};

export default ProductServiceAutocomplete;
